/** Page furniture: the header, tabs, stat tiles and the pager. */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, TrendingDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { formatNumber } from '../../lib/format';
import { Button } from './button';
import { ScrollArea } from './scroll-area';

// ── page header ─────────────────────────────────────────────
export interface Breadcrumb {
  label: string;
  to?: string;
}

/**
 * The top of every page, and the part of it that stays put.
 *
 * Consistent placement is the whole value: the title, the breadcrumb trail and
 * the primary action are always in the same three places, so a person moving
 * between twenty registers never hunts for the New button.
 *
 * **It is fixed, the page scrolls under it** — with the page's `tabs`, which
 * belong to it. Every page scrolls in the shell's one `ScrollArea`, so the
 * header is `sticky` to that area rather than a second scroll region the page
 * would have to be built around: any page, whatever its structure, keeps its
 * title, actions and tabs in view. It spans the shell's gutter
 * (`--page-gutter`, set on the shell's `main`) so nothing shows past its edges.
 *
 * On a phone a tall header would eat the screen, so once the page has scrolled
 * the breadcrumbs and the description fold away and only the title, the
 * actions and the tabs stay.
 *
 * How much of the top it covers is published as `--page-sticky-top` on the
 * page, for what sticks below it (``Sections``' menu) and for
 * `scroll-margin-top`, so a section scrolled to is not hidden beneath it.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  tabs,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: readonly Breadcrumb[];
  /** The primary action, and any secondary ones. Right-aligned. */
  actions?: ReactNode;
  /** The page's tab strip — fixed with the header. */
  tabs?: ReactNode;
  className?: string;
}) {
  const block = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useLayoutEffect(() => {
    const element = block.current;
    const page = element?.parentElement;
    if (!element || !page || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => page.style.setProperty('--page-sticky-top', `${element.offsetHeight}px`);
    publish();
    const watch = new ResizeObserver(publish);
    watch.observe(element);
    return () => {
      watch.disconnect();
      page.style.removeProperty('--page-sticky-top');
    };
  }, []);

  useEffect(() => {
    // Stuck once the page has scrolled at all: the header sits at the very top
    // of the page, so from then on something is passing under it — and only
    // then does it draw a line under itself (and fold, on a phone).
    const area = block.current?.closest('.simplebar-content-wrapper');
    if (!area) return undefined;
    const check = () => setStuck(area.scrollTop > 0);
    check();
    area.addEventListener('scroll', check, { passive: true });
    return () => area.removeEventListener('scroll', check);
  }, []);

  return (
    <div
      ref={block}
      data-stuck={stuck || undefined}
      className={cn(
        'group/header sticky top-0 z-page-sticky -mt-1 bg-background pt-1',
        'mx-[calc(var(--page-gutter,1rem)*-1)] px-(--page-gutter,1rem)',
        'data-stuck:shadow-[0_1px_0_var(--color-line)]',
        !tabs && 'pb-3',
        className,
      )}
    >
      <header className="space-y-2">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <nav aria-label="Breadcrumb" className="max-md:group-data-stuck/header:hidden">
              <ol className="flex flex-wrap items-center gap-1 text-xs text-muted">
                {breadcrumbs.map((crumb, index) => (
                  <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                    {index > 0 ? <ChevronRight className="size-3 text-faint" aria-hidden /> : null}
                    {crumb.to ? (
                      <Link to={crumb.to} className="hover:text-accent hover:underline">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-faint">{crumb.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          ) : null}

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <h1 className="truncate text-lg font-semibold tracking-tight text-heading">{title}</h1>
              {description ? (
                <p className="max-w-2xl text-sm text-muted max-md:group-data-stuck/header:hidden">{description}</p>
              ) : null}
            </div>
            {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
          </div>
      </header>
      {tabs ? <div className="mt-3">{tabs}</div> : null}
    </div>
  );
}

// ── tabs ────────────────────────────────────────────────────
export interface TabItem {
  id: string;
  label: ReactNode;
  /** A count beside the label — how many rows the tab holds. */
  count?: number;
  disabled?: boolean;
}

/**
 * A tab strip.
 *
 * Uncontrolled by default and controllable when the page needs the active tab
 * in the URL — which a detail page does, so a link to a specific tab can be
 * shared.
 */
export function Tabs({
  items,
  value,
  onChange,
  className,
}: {
  items: readonly TabItem[];
  value?: string;
  onChange?: (id: string) => void;
  className?: string;
}) {
  const [internal, setInternal] = useState(items[0]?.id ?? '');
  const active = value ?? internal;

  const select = (id: string) => {
    setInternal(id);
    onChange?.(id);
  };

  return (
    // The strip scrolls sideways when the tabs outgrow it; the border is on the
    // row inside, so the selected tab's indicator can sit on it unclipped.
    <ScrollArea className={className}>
      <div className="flex gap-1 border-b border-line" role="tablist">
      {items.map((item) => {
        const selected = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={item.disabled}
            onClick={() => select(item.id)}
            className={cn(
              'relative shrink-0 px-3 py-2 text-sm font-medium transition-colors',
              'disabled:pointer-events-none disabled:opacity-45',
              selected ? 'text-accent' : 'text-muted hover:text-body',
            )}
          >
            <span className="flex items-center gap-1.5">
              {item.label}
              {item.count !== undefined ? (
                <span
                  className={cn(
                    'rounded-pill px-1.5 py-px text-2xs tabular',
                    selected ? 'bg-accent-subtle text-accent' : 'bg-surface-2 text-faint',
                  )}
                >
                  {formatNumber(item.count)}
                </span>
              ) : null}
            </span>
            {/* The indicator is a sibling element rather than a bottom border,
                so it can sit on top of the strip's own border instead of
                shifting the text by a pixel when it appears. */}
            {selected ? (
              <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-accent" aria-hidden />
            ) : null}
          </button>
        );
      })}
      </div>
    </ScrollArea>
  );
}

// ── stat tile ───────────────────────────────────────────────
/**
 * One number on a dashboard.
 *
 * The delta is optional and carries its own direction, because "up 4%" is good
 * news for revenue and bad news for overdue invoices — so the caller says which
 * way is good rather than the tile assuming.
 */
export function StatCard({
  label,
  value,
  hint,
  delta,
  icon,
  tone = 'neutral',
  to,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  delta?: { value: string; direction: 'up' | 'down'; good: boolean };
  icon?: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
  /** Makes the whole tile a link — a KPI is usually a question with a page behind it. */
  to?: string;
}) {
  const accents = {
    neutral: 'text-faint',
    accent: 'text-accent',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  } as const;

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-muted">{label}</p>
        {icon ? <span className={cn('shrink-0', accents[tone])}>{icon}</span> : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-heading tabular">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {delta ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-2xs font-medium',
              delta.good ? 'text-success' : 'text-danger',
            )}
          >
            {delta.direction === 'up' ? (
              <TrendingUp className="size-3" aria-hidden />
            ) : (
              <TrendingDown className="size-3" aria-hidden />
            )}
            {delta.value}
          </span>
        ) : null}
        {hint ? <span className="truncate text-2xs text-faint">{hint}</span> : null}
      </div>
    </>
  );

  const shell = 'rounded-card border border-line bg-surface p-4 shadow-card';

  if (to) {
    return (
      <Link to={to} className={cn(shell, 'block transition-shadow hover:shadow-raised')}>
        {body}
      </Link>
    );
  }
  return <div className={shell}>{body}</div>;
}

/** A responsive row of stat tiles. */
export function StatRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid gap-3 sm:grid-cols-2 xl:grid-cols-4', className)}>{children}</div>
  );
}

// ── pager ───────────────────────────────────────────────────
export const PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * The pager under a register.
 *
 * Shows the range and the total, not just the page number: "1–25 of 1,284" tells
 * somebody how much is there, which is the question a bare "Page 1" leaves open.
 * First and last buttons are included because jumping to the end of a sorted
 * register is a real thing people do and clicking Next forty times is not.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  className?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-line px-3 py-2',
        className,
      )}
    >
      <p className="text-xs text-muted tabular">
        {total === 0 ? (
          'No records'
        ) : (
          <>
            <span className="font-medium text-body">
              {formatNumber(from)}–{formatNumber(to)}
            </span>{' '}
            of {formatNumber(total)}
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        {onPageSizeChange ? (
          <label className="flex items-center gap-1.5 text-xs text-muted">
            <span className="hidden sm:inline">Rows</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className="h-7 cursor-pointer rounded-control border border-line bg-surface px-1.5 text-xs text-body"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={page <= 1}
            onClick={() => onPageChange(1)}
            aria-label="First page"
          >
            <ChevronsLeft className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="px-2 text-xs text-muted tabular">
            {formatNumber(page)} / {formatNumber(pages)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={page >= pages}
            onClick={() => onPageChange(pages)}
            aria-label="Last page"
          >
            <ChevronsRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
